import React, { useState } from "react";
import { Box, Button, Grid, TextField, Typography, FormControl, InputLabel, Select, MenuItem } from "@mui/material";
import dayjs from "dayjs";
import MyDatePicker from "../../atoms/Common/MyDatePicker";
import { getReceiptAcknowledgements, programPayments, exportReceiptAcknowledgementTxt, exportReceiptAcknowledgementCsv } from "../../api/reciptAcknowledgement";

export default function RAExportModal({ companyId, onSubmit, onProgramPayments, onCsvExport }) {
  const [filterType, setFilterType] = useState('dateRange'); // 'dateRange', 'codeRange', 'paymentDate', 'singlePaymentDate'
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [codeStart, setCodeStart] = useState(null);
  const [codeEnd, setCodeEnd] = useState(null);
  const [paymentStartDate, setPaymentStartDate] = useState(null);
  const [paymentEndDate, setPaymentEndDate] = useState(null);
  const [singlePaymentDate, setSinglePaymentDate] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (loading) return; // Prevent double-clicks
    
    try {
      setLoading(true);
      
      const exportParams = {
        companyId,
      };

      switch (filterType) {
        case 'dateRange':
          exportParams.startDate = startDate?.format("YYYY-MM-DD");
          exportParams.endDate = endDate?.format("YYYY-MM-DD");
          break;
        case 'codeRange':
          exportParams.codeStart = codeStart;
          exportParams.codeEnd = codeEnd;
          break;
        case 'paymentDate':
          exportParams.paymentStartDate = paymentStartDate?.format("YYYY-MM-DD");
          exportParams.paymentEndDate = paymentEndDate?.format("YYYY-MM-DD");
          break;
        case 'singlePaymentDate':
          exportParams.paymentStartDate = singlePaymentDate?.format("YYYY-MM-DD");
          exportParams.paymentEndDate = singlePaymentDate?.format("YYYY-MM-DD");
          break;
      }

      const response = await exportReceiptAcknowledgementTxt(exportParams);
      
      // Create download link - response.data is already a blob
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `export_${dayjs().format('YYYYMMDD_HHmmss')}.txt`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      onSubmit(); // Close modal after success
    } catch (error) {
      console.error('Error exporting:', error);
      alert(error.response?.data?.Details || error.message || 'Error al exportar');
    } finally {
      setLoading(false);
    }
  };

  const handleProgramPayments = async () => {
    if (loading) return; // Prevent double-clicks
    
    try {
      setLoading(true);
      
      // Get IDs based on the selected filter type
      const criteria = {
        companyId,
        pageSize: 1000,
      };

      switch (filterType) {
        case 'dateRange':
          if (!startDate || !endDate) {
            throw new Error('Please set both start and end dates');
          }
          criteria.startDate = startDate.format("YYYY-MM-DD");
          criteria.endDate = endDate.format("YYYY-MM-DD");
          break;
        case 'codeRange':
          if (!codeStart || !codeEnd) {
            throw new Error('Please set both start and end codes');
          }
          criteria.codeStart = codeStart;
          criteria.codeEnd = codeEnd;
          break;
        case 'paymentDate':
          if (!paymentStartDate || !paymentEndDate) {
            throw new Error('Please set both payment start and end dates');
          }
          criteria.paymentStartDate = paymentStartDate.format("YYYY-MM-DD");
          criteria.paymentEndDate = paymentEndDate.format("YYYY-MM-DD");
          break;
        case 'singlePaymentDate':
          if (!singlePaymentDate) {
            throw new Error('Please set a payment date');
          }
          criteria.paymentStartDate = singlePaymentDate.format("YYYY-MM-DD");
          criteria.paymentEndDate = singlePaymentDate.format("YYYY-MM-DD");
          break;
      }
      
      const response = await programPayments(criteria);
      const { processedReceiptsCount, ignoredReceiptsCount } = response.data;
      let message = `Pagos programados exitosamente para ${processedReceiptsCount} contrarrecibo(s)`;
      if (ignoredReceiptsCount > 0) {
        message += `. ${ignoredReceiptsCount} contrarrecibo(s) fueron omitidos (no estaban en estado Creado)`;
      }
      alert(message);
      
      // Call the program payments callback instead of onSubmit
      if (onProgramPayments) {
        onProgramPayments();
      }
    } catch (error) {
      console.error('Error programming payments:', error);
      alert(error.response?.data?.Details || error.message || 'Error al programar pagos');
    } finally { 
      setLoading(false);
    }
  };

  const handleExportCsv = async () => {
    if (loading) return; // Prevent double-clicks
    
    try {
      setLoading(true);
      
      const exportParams = {
        companyId,
      };

      switch (filterType) {
        case 'dateRange':
          exportParams.startDate = startDate?.format("YYYY-MM-DD");
          exportParams.endDate = endDate?.format("YYYY-MM-DD");
          break;
        case 'codeRange':
          exportParams.codeStart = codeStart;
          exportParams.codeEnd = codeEnd;
          break;
        case 'paymentDate':
          exportParams.paymentStartDate = paymentStartDate?.format("YYYY-MM-DD");
          exportParams.paymentEndDate = paymentEndDate?.format("YYYY-MM-DD");
          break;
        case 'singlePaymentDate':
          exportParams.paymentStartDate = singlePaymentDate?.format("YYYY-MM-DD");
          exportParams.paymentEndDate = singlePaymentDate?.format("YYYY-MM-DD");
          break;
      }

      const response = await exportReceiptAcknowledgementCsv(exportParams);
      
      // Create download link - response.data is already a blob
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `export_${dayjs().format('YYYYMMDD_HHmmss')}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      // Close modal after success (use onCsvExport to avoid triggering TXT export)
      if (onCsvExport) {
        onCsvExport();
      }
    } catch (error) {
      console.error('Error exporting CSV:', error);
      alert(error.response?.data?.Details || error.message || 'Error al exportar CSV');
    } finally {
      setLoading(false);
    }
  };

  const isFilterValid = () => {
    switch (filterType) {
      case 'dateRange':
        return startDate && endDate;
      case 'codeRange':
        return codeStart && codeEnd;
      case 'paymentDate':
        return paymentStartDate && paymentEndDate;
      case 'singlePaymentDate':
        return !!singlePaymentDate;
      default:
        return false;
    }
  };

  return (
    <Box>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <FormControl fullWidth>
            <InputLabel>Tipo de filtro</InputLabel>
            <Select
              value={filterType}
              label="Tipo de filtro"
              onChange={(e) => setFilterType(e.target.value)}
            >
              <MenuItem value="dateRange">Por rango de fechas</MenuItem>
              <MenuItem value="codeRange">Por rango de contrarrecibos</MenuItem>
              <MenuItem value="paymentDate">Por rango de fechas de pago</MenuItem>
              <MenuItem value="singlePaymentDate">Por fecha de pago</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {filterType === 'dateRange' && (
          <>
            <Grid item xs={12}>
              <Typography variant="h6">Por fecha</Typography>
            </Grid>
            <Grid item xs={6}>
              <MyDatePicker
                fullWidth
                slotProps={{ textField: { fullWidth: true } }}
                label="Fecha inicial"
                value={startDate}
                onChange={(value) => setStartDate(value)}
              />
            </Grid>
            <Grid item xs={6}>
              <MyDatePicker
                fullWidth
                slotProps={{ textField: { fullWidth: true } }}
                label="Fecha final"
                value={endDate}
                onChange={(value) => setEndDate(value)}
              />
            </Grid>
          </>
        )}

        {filterType === 'codeRange' && (
          <>
            <Grid item xs={12}>
              <Typography variant="h6">Por rango de contrarrecibos</Typography>
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="No. contrarrecibo inicial"
                value={codeStart}
                onChange={(e) => setCodeStart(e.target.value)}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="No. contrarrecibo final"
                value={codeEnd}
                onChange={(e) => setCodeEnd(e.target.value)}
              />
            </Grid>
          </>
        )}

        {filterType === 'paymentDate' && (
          <>
            <Grid item xs={12}>
              <Typography variant="h6">Por rango de fechas de pago</Typography>
            </Grid>
            <Grid item xs={6}>
              <MyDatePicker
                fullWidth
                slotProps={{ textField: { fullWidth: true } }}
                label="Fecha de pago inicial"
                value={paymentStartDate}
                onChange={(value) => setPaymentStartDate(value)}
              />
            </Grid>
            <Grid item xs={6}>
              <MyDatePicker
                fullWidth
                slotProps={{ textField: { fullWidth: true } }}
                label="Fecha de pago final"
                value={paymentEndDate}
                onChange={(value) => setPaymentEndDate(value)}
              />
            </Grid>
          </>
        )}

        {filterType === 'singlePaymentDate' && (
          <>
            <Grid item xs={12}>
              <Typography variant="h6">Por fecha de pago</Typography>
            </Grid>
            <Grid item xs={12}>
              <MyDatePicker
                fullWidth
                slotProps={{ textField: { fullWidth: true } }}
                label="Fecha de pago"
                value={singlePaymentDate}
                onChange={(value) => setSinglePaymentDate(value)}
              />
            </Grid>
          </>
        )}

        <Grid item xs={12}>
          <Button
            fullWidth
            variant="contained"
            onClick={handleSubmit}
            disabled={loading || !isFilterValid()}
          >
            {loading ? 'Exportando...' : 'Exportar TXT de pólizas para Contpaq'}
          </Button>
        </Grid>
        <Grid item xs={12}>
          <Button
            fullWidth
            variant="contained"
            color="secondary"
            onClick={handleProgramPayments}
            disabled={loading || !isFilterValid()}
          >
            {loading ? 'Procesando...' : 'Proponer a pago'}
          </Button>
        </Grid>
        <Grid item xs={12}>
          <Button
            fullWidth
            variant="outlined"
            color="primary"
            onClick={handleExportCsv}
            disabled={loading || !isFilterValid()}
          >
            {loading ? 'Exportando...' : 'Exportar desglose de contrarecibos a CSV'}
          </Button>
        </Grid>

      </Grid>
    </Box>
  );
}
